// components/OptionsInstrumentSection.jsx
//
// Instrument switch for Alpaca stock bots: trade SHARES (default) or single-leg
// OPTIONS driven by the same strategy signals (long signals buy calls, short
// signals buy puts). Self-gating like ExtendedHoursToggle — mounted
// unconditionally, renders null off stock_equity — so the wizard needs no
// conditional JSX at the call site.
import React from 'react';
import {
  Box, Paper, Typography, ToggleButtonGroup, ToggleButton,
  TextField, MenuItem, Alert,
} from '@mui/material';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : '');

export default function OptionsInstrumentSection({
  assetClass,
  value = {},              // { instrument, options: {...} } slice of newBot
  onChange,                // (patch) => void — merges into newBot
  optionsApprovedLevel = null,  // from the alpaca account status, when known
}) {
  if (assetClass !== 'stock_equity') return null;

  const instrument = value.instrument === 'options' ? 'options' : 'shares';
  const o = {
    right: 'auto', selection: 'delta', targetDelta: 0.5, moneynessPct: 0,
    dteMin: 20, dteMax: 45, exitDteFloor: 7, minOpenInterest: 10,
    ...(value.options || {}),
  };
  const patchOpt = (p) => onChange({ options: { ...o, ...p } });

  const dteInvalid = Number(o.dteMin) < Number(o.exitDteFloor) + 1;
  const levelKnownTooLow = optionsApprovedLevel != null && Number(optionsApprovedLevel) < 2;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 600 }}>Instrument</Typography>
          <Typography variant="caption" color="text.secondary">
            {instrument === 'options'
              ? 'Signals trade option contracts on this symbol — long signals buy calls, short signals buy puts.'
              : 'Signals trade shares of this symbol.'}
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive size="small" value={instrument}
          onChange={(_, v) => { if (v) onChange({ instrument: v }); }}
        >
          <ToggleButton value="shares">Shares</ToggleButton>
          <ToggleButton value="options">Options</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {instrument === 'options' && (
        <Box sx={{ mt: 2 }}>
          {levelKnownTooLow && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This Alpaca account's options approval level is {optionsApprovedLevel} — buying calls/puts
              needs Level 2. Enable options trading in your Alpaca dashboard or orders will be rejected.
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            <TextField
              select size="small" label="Right" value={o.right}
              onChange={(e) => patchOpt({ right: e.target.value })}
              helperText={o.right === 'put'
                ? 'puts are bought on SHORT signals — needs Allow Shorts + short logic'
                : o.right === 'auto' ? 'calls on buys, puts on shorts (puts need short logic)' : 'calls only'}
            >
              <MenuItem value="auto">Auto</MenuItem>
              <MenuItem value="call">Calls only</MenuItem>
              <MenuItem value="put">Puts only</MenuItem>
            </TextField>
            <TextField
              select size="small" label="Strike by" value={o.selection}
              onChange={(e) => patchOpt({ selection: e.target.value })}
            >
              <MenuItem value="delta">Target delta</MenuItem>
              <MenuItem value="moneyness">Moneyness %</MenuItem>
            </TextField>
            {o.selection === 'delta' ? (
              <TextField
                size="small" label="Target delta" type="number" value={num(o.targetDelta)}
                inputProps={{ min: 0.05, max: 0.95, step: 0.05 }}
                onChange={(e) => patchOpt({ targetDelta: e.target.value })}
                helperText="0.5 ≈ at-the-money"
              />
            ) : (
              <TextField
                size="small" label="Moneyness %" type="number" value={num(o.moneynessPct)}
                inputProps={{ min: -50, max: 50, step: 1 }}
                onChange={(e) => patchOpt({ moneynessPct: e.target.value })}
                helperText="0 = ATM; + is above spot"
              />
            )}
            <TextField
              size="small" label="Min open interest" type="number" value={num(o.minOpenInterest)}
              inputProps={{ min: 0, step: 10 }}
              onChange={(e) => patchOpt({ minOpenInterest: e.target.value })}
            />
            <TextField
              size="small" label="DTE min" type="number" value={num(o.dteMin)}
              inputProps={{ min: 1, max: 365 }} error={dteInvalid}
              onChange={(e) => patchOpt({ dteMin: e.target.value })}
            />
            <TextField
              size="small" label="DTE max" type="number" value={num(o.dteMax)}
              inputProps={{ min: 1, max: 400 }}
              error={Number(o.dteMax) < Number(o.dteMin)}
              onChange={(e) => patchOpt({ dteMax: e.target.value })}
            />
            <TextField
              size="small" label="Exit DTE floor" type="number" value={num(o.exitDteFloor)}
              inputProps={{ min: 0, max: 60 }} error={dteInvalid}
              onChange={(e) => patchOpt({ exitDteFloor: e.target.value })}
              helperText={dteInvalid ? 'DTE min must be ≥ floor + 1' : 'close when DTE drops below'}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Buy-only (defined risk): positions are long calls or long puts, one contract position at a
            time, closed by your exit signals, premium stop/target, the DTE floor, or expiry.
            Orders are market, regular hours only.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
